import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { normalizeError } from "@/store/helpers/errorHelper";

export interface AdministrativeFormInfo {
  errors: unknown;
}

@Module({ namespaced: true, name: "AdministrativeFormModule" })
export default class AdministrativeFormModule
  extends VuexModule
  implements AdministrativeFormInfo
{
  errors = {};

  get getError() {
    const [errorName] = Object.keys(this.errors);
    const error = this.errors[errorName];
    return error;
  }

  @Mutation
  [Mutations.SET_ERROR](errorObj) {
    this.errors = normalizeError(errorObj);
  }

  @Action
  [Actions.GET_MY_ADMINISTRATIVE_FORMS]() {
    return ApiService.get("/administrative_forms/me")
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
        throw response;
      });
  }

  @Action
  [Actions.SAVE_GOODS_MANAGEMENT_POLICY](data) {
    const body = {
      hasReadGoodsManagementAndResponsibilityPolicy:
        data.hasReadGoodsManagementAndResponsibilityPolicy,
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_goods_management_policy`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_CHSLD_CONSENT](data) {
    const body = {
      chsldConsentSignature: {
        fullName: data.generalConsentSignatory,
        signedAt: data.generalConsentSignatureDate,
        hasConsented: data.generalConsentAgreed,
      },
      chsldTreatmentConsentDoctorFullName: data.examConsentDoctorName,
      chsldTreatmentConsentTreatmentDescription: data.examConsentDescription,
      chsldTreatmentConsentSignature:
        data.examConsentAgreed === true
          ? {
              fullName: data.examConsentSignatory,
              signedAt: data.examConsentSignatureDate,
              hasConsented: data.examConsentAgreed,
            }
          : null,
      chsldTreatmentRefusalTreatmentDescription: data.refusalDescription,
      chsldTreatmentRefusalTreatmentRecommendedBy: data.refusalRecommendedBy,
      chsldTreatmentRefusalSignature:
        data.refusalAgreed === true
          ? {
              fullName: data.refusalSignatory,
              signedAt: data.refusalSignatureDate,
              hasConsented: data.refusalAgreed,
            }
          : null,
      chsldDepartureWithoutDischargeSignature:
        data.departureAgreed === true
          ? {
              fullName: data.departureSignatory,
              signedAt: data.departureSignatureDate,
              hasConsented: data.departureAgreed,
            }
          : null,
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_chsld_consent`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SIGN_CHSLD_CONSENT](data) {
    const body = {
      ChsldConsentWitnessSignature: {
        fullName: data.chsldConsentWitnessFullName,
        signedAt: data.chsldConsentWitnessSignatureProvidedAt,
        hasConsented: data.chsldConsentWitnessConsentProvided,
      },
      ChsldTreatmentConsentWitnessSignature:
        data.chsldTreatmentConsentWitnessConsentProvided === true
          ? {
              fullName: data.chsldTreatmentConsentWitnessFullName,
              signedAt: data.chsldTreatmentConsentWitnessSignatureProvidedAt,
              hasConsented: data.chsldTreatmentConsentWitnessConsentProvided,
            }
          : null,
      ChsldTreatmentRefusalWitnessSignature:
        data.chsldTreatmentRefusalWitnessConsentProvided === true
          ? {
              fullName: data.chsldTreatmentRefusalWitnessFullName,
              signedAt: data.chsldTreatmentRefusalWitnessSignatureProvidedAt,
              hasConsented: data.chsldTreatmentRefusalWitnessConsentProvided,
            }
          : null,
      ChsldDepartureWithoutDischargeWitnessSignature:
        data.chsldDepartureWithoutDischargeWitnessConsentProvided === true
          ? {
              fullName: data.chsldDepartureWithoutDischargeWitnessFullName,
              signedAt:
                data.chsldDepartureWithoutDischargeWitnessSignatureProvidedAt,
              hasConsented:
                data.chsldDepartureWithoutDischargeWitnessConsentProvided,
            }
          : null,
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/sign_chsld_consent`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_DIGITAL_CONTENT_CONSENT](data) {
    // FormData encode undefined and null as string value, so we want to set an empty string instead
    Object.keys(data).forEach((key) => {
      if (data[key] === undefined || data[key] === null) {
        data[key] = "";
      }
    });
    const formData = new FormData();
    formData.append("ModalitySignature.FullName", data.modalitySignatory);
    formData.append("ModalitySignature.SignedAt", data.modalitySignatureDate);
    formData.append("ModalitySignature.HasConsented", data.modalityAgreed);

    formData.append(
      "hasConsentedToPersonalDigitalContentForClinicalUsage",
      data.clinicalUsageAuthorization === "Oui" ? "true" : "false"
    );
    formData.append(
      "hasConsentedToPersonalDigitalContentPublication",
      data.socialUsageAuthorization === "Oui" ? "true" : "false"
    );
    formData.append(
      "anonymizeIdentityDuringPublication",
      data.anonymityPreference === "Oui" ? "true" : "false"
    );
    if (data.residentPhotoFile !== "") {
      formData.append(
        "PictureFile",
        data.residentPhotoFile,
        data.residentPhotoFile.name
      );
    }

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_digital_content_consent`,
      formData
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SIGN_DIGITAL_CONTENT_CONSENT](data) {
    const body = {
      ModalityWitnessSignature: {
        fullName: data.modalityWitnessFullName,
        signedAt: data.modalityWitnessSignatureProvidedAt,
        hasConsented: data.modalityWitnessConsentProvided,
      },
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/sign_digital_content_consent`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_CLOTHES_CLEANING_SELECTION](data) {
    const body = {
      clothesCleaningBasicService: data.clothesCleaningBasicService,
      clothesCleaningByFamilyOnSite: data.clothesCleaningByFamilyOnSite,
      clothesCleaningByFamilyOffSite: data.clothesCleaningByFamilyOffSite,
      clothesCleaningCustomService: data.clothesCleaningCustomService,
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_clothes_cleaning_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_TELECOM_UTILITIES_SELECTION](data) {
    const body = {
      televisionServiceSelected: data.televisionServiceSelected,
      internetServiceSelected: data.internetServiceSelected,
      telephoneServiceSelected: data.telephoneServiceSelected,
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_telecom_utilities_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_HAIR_SALON_SELECTION](data) {
    let body = {
      hairSalonServiceSelected: data.hairSalonServiceSelected,
    };

    if (data.hairSalonServiceSelected) {
      const restOfBody = {
        hairSettlingServiceSelected:
          data.hairSettlingFrequency === "none" ? false : true,
        hairSettlingFrequency:
          data.hairSettlingFrequency === "none"
            ? null
            : data.hairSettlingFrequency,
        haircutServiceSelected: data.haircutFrequency === "none" ? false : true,
        haircutFrequency:
          data.haircutFrequency === "none" ? null : data.haircutFrequency,
        hairColoringServiceSelected:
          data.hairColoringFrequency === "none" ? false : true,
        hairColoringFrequency:
          data.hairColoringFrequency === "none"
            ? null
            : data.hairColoringFrequency,
        hairPermanentServiceSelected:
          data.hairPermanentFrequency === "none" ? false : true,
        hairPermanentFrequency:
          data.hairPermanentFrequency === "none"
            ? null
            : data.hairPermanentFrequency,
        menHaircutServiceSelected:
          data.menHaircutFrequency === "none" ? false : true,
        menHaircutFrequency:
          data.menHaircutFrequency === "none" ? null : data.menHaircutFrequency,
        hairSalonTransportServiceSelected:
          data.hairSalonTransportServiceSelected === "none" ? false : true,
        hairSalonTipSelected: data.hairSalonTipAmount ? true : false,
        hairSalonTipAmount: data.hairSalonTipAmount,
      };

      body = { ...body, ...restOfBody };
    }

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_hair_salon_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_MANICURE_AND_PEDICURE_SELECTION](data) {
    let body = {
      manicureAndPedicureServiceSelected:
        data.manicureAndPedicureServiceSelected,
    };
    if (data.manicureAndPedicureServiceSelected) {
      const restOfBody = {
        pedicureServiceSelected:
          data.pedicureFrequency === "none" ? false : true,
        pedicureFrequency:
          data.pedicureFrequency === "none" ? null : data.pedicureFrequency,
        manicureServiceSelected:
          data.manicureFrequency === "none" ? false : true,
        manicureFrequency:
          data.manicureFrequency === "none" ? null : data.manicureFrequency,
      };

      body = { ...body, ...restOfBody };
    }

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_manicure_and_pedicure_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SAVE_PAID_ACTIVITY_SELECTION](data) {
    const body = {
      paidActivitySelected: data.paidActivitySelected,
      payByPreAuthorizedDebit: data.payByPreAuthorizedDebit,
      payByCash: data.payByCash,
      serviceSignature: {
        fullName: data.serviceSignatoryFullName,
        signedAt: data.serviceSignatureProvidedAt,
        hasConsented: data.serviceConsentProvided,
      },
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/save_paid_activity_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.SIGN_PAID_ACTIVITY_SELECTION](data) {
    const body = {
      serviceWitnessSignature: {
        fullName: data.serviceWitnessFullName,
        signedAt: data.serviceWitnessSignatureProvidedAt,
        hasConsented: data.serviceWitnessConsentProvided,
      },
    };

    return ApiService.patch(
      `/administrative_forms/${data.administrativeFormId}/sign_paid_activity_selection`,
      body
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_HAIR_SALON_SERVICE_FREQUENCY]() {
    return ApiService.get(`/hair_salon_service_frequency`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_MANICURE_AND_PEDICURE_SERVICE_FREQUENCY]() {
    return ApiService.get(`/manicure_and_pedicure_service_frequency`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }

  @Action
  [Actions.GET_ADMINISTRATIVE_FORM_BY_ID](id) {
    return ApiService.get(`/administrative_forms/${id}`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ERROR, null);
        return data;
      })
      .catch(({ response, message }) => {
        this.context.commit(Mutations.SET_ERROR, { response, message });
      });
  }
}
